import './kitStyles';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { flFix, tipLayerPlace } from './floatPlace';
import { isKeyboardInput } from './inputMode';
import { layerBase, layerMode, layerRoot } from './layerScope';

/**
 * WEB-3310 юнит 2а-1 — подсказка `data-tip` (эталон: общий слой `.tip-layer`,
 * `flTipShow` / `flTipHide` и обработчики `flInit` в `design/cabinet-rescin/kit.jsx`).
 * Не `::after` внутри кнопки (его резали таблица, карточка и тело окна), а один
 * узел `position: fixed` поверх всего: над хозяином, нет места — под ним, к краям
 * экрана не ближе 8 px (`tipLayerPlace`). Слой ложится в `dialog[open]` хозяина,
 * иначе в свой корень `.ds` (шрифт и цвета заданы на `.ds`, в `body` подсказка
 * теряла их).
 *
 * Ставится один раз одним из двух способов (правило выбора корня — `layerScope.ts`):
 *  - владелец корня `.ds` внутри корня (витрина `/kit`) — судит только хозяев своего
 *    корня (решение оркестратора 23.10), наблюдатель изменений (хозяин удалён,
 *    сменился `data-tip`) — на корне;
 *  - оболочка кабинета `DashboardLayout` вне `.ds` с `everyRoot` — судит хозяина с
 *    любым ближайшим `.ds`, слой ложится в его корень; хозяин без предка `.ds`
 *    (страницы antd) не судится; наблюдатель — на `document.body`.
 * Показ — наведение и фокус с клавиатуры (`isKeyboardInput()` и `:focus-visible`,
 * эталон 24.10); скрытие — уход на
 * место без подсказки, Esc, `mousedown`, уход фокуса, смена размера, хозяин
 * удалён; прокрутка — переставить.
 */
export function TipLayer({ everyRoot = false }: { everyRoot?: boolean } = {}): React.ReactElement {
  const mark = useRef<HTMLSpanElement>(null);
  const layer = useRef<HTMLSpanElement>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [text, setText] = useState('');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const mode = layerMode(mark.current, everyRoot);
    if (!mode) return undefined;
    let cur: HTMLElement | null = null;
    const show = (h: HTMLElement) => {
      cur = h;
      setHost(h);
      setText(h.getAttribute('data-tip') || '');
      setTick((n) => n + 1);
    };
    const hide = () => {
      if (!cur) return;
      cur = null;
      setHost(null);
    };
    const owner = (t: EventTarget | null): HTMLElement | null => {
      const h = t instanceof Element ? t.closest<HTMLElement>('[data-tip]') : null;
      return h && layerRoot(h, mode) && h.getAttribute('data-tip') ? h : null;
    };
    const over = (e: Event) => {
      const h = owner(e.target);
      if (h) {
        if (h !== cur) show(h);
      } else hide();
    };
    const focusin = (e: Event) => {
      const h = owner(e.target);
      if (h && isKeyboardInput() && (e.target as Element).matches(':focus-visible')) show(h);
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    const scroll = () => {
      if (cur) setTick((n) => n + 1);
    };
    const mo = new MutationObserver((ms) => {
      if (!cur) return;
      if (!cur.isConnected) return hide();
      if (ms.some((m) => m.type === 'attributes' && m.target === cur)) {
        const t = cur.getAttribute('data-tip');
        if (t) show(cur);
        else hide();
      }
    });
    mo.observe(layerBase(mode), { childList: true, subtree: true, attributes: true, attributeFilter: ['data-tip'] });
    document.addEventListener('mouseover', over, true);
    document.addEventListener('focusin', focusin, true);
    document.addEventListener('focusout', hide, true);
    document.addEventListener('mousedown', hide, true);
    document.addEventListener('keydown', key, true);
    window.addEventListener('resize', hide);
    window.addEventListener('scroll', scroll, true);
    return () => {
      mo.disconnect();
      document.removeEventListener('mouseover', over, true);
      document.removeEventListener('focusin', focusin, true);
      document.removeEventListener('focusout', hide, true);
      document.removeEventListener('mousedown', hide, true);
      document.removeEventListener('keydown', key, true);
      window.removeEventListener('resize', hide);
      window.removeEventListener('scroll', scroll, true);
    };
  }, [everyRoot]);

  useLayoutEffect(() => {
    const el = layer.current;
    if (!host || !el) return;
    el.style.left = '-9999px';
    el.style.top = '0px';
    const p = tipLayerPlace(
      host.getBoundingClientRect(),
      el.offsetWidth,
      el.offsetHeight,
      window.innerWidth,
      window.innerHeight,
    );
    flFix(el, p.x, p.y);
  }, [host, text, tick]);

  const mode = host ? layerMode(mark.current, everyRoot) : null;
  const box = host && mode ? host.closest('dialog[open]') || layerRoot(host, mode) : null;
  return (
    <span ref={mark} hidden>
      {box &&
        createPortal(
          <span ref={layer} className="tip-layer" role="tooltip">
            {text}
          </span>,
          box,
        )}
    </span>
  );
}
